import React from "react";
import DevilsQuadrangleChart, { axesFromDevilsQuadrangle } from "./DevilsQuadrangleChart";
import {
  subprocessBadgesByHeuristic,
  lookupSubprocessBadge,
  subprocessBadgesByCodeFromProjection,
  lookupSubprocessBadgeByCode,
  projectedParentKpis,
  projectionPricedIntoKpis,
  parentRollupUnavailableReason,
  DEFAULT_INCLUDE_SUBPROCESS,
} from "../../utils/subprocessRedesign";
import { ChildChip, ProjectedColumnBadge, ProjectionBanner } from "./SubprocessProjectionBits";
import SubprocessChildResults from "./SubprocessChildResults";

// Renders GET /cms/comparison-report/{id} — greedy, RL and BPR run against one
// shared AS-IS baseline. State (result/running/etc.) is owned by the page
// (useComparisonReportData lifted there), same pattern as BprRedesignPanel /
// RlRedesignPanel, so the last report survives switching tabs.
//
// The rendering rules below are not style choices — they are called out
// explicitly in COMPARISON_REPORT.md because getting them wrong makes the UI
// state something false:
//   Rule 1 — null is never zero (kpis/devils_quadrangle `to_be` can be null;
//            that means "could not be honestly placed on this axis")
//   Rule 2 — engines[].native is audit-only, never mixed into the shared chart
//   Rule 3 — measured (time/cost) vs estimated (quality/flexibility) must look
//            visually distinct
//   Rule 4 — dq_coverage.complete === false means the quadrangle is partial,
//            not the engine's full effect
//   Rule 5 — heuristic_matrix rows with agreement >= 2 are the strongest signal
//            in the report and need real visual weight, not just a matching color
//   Rule 6 — impact_computable is per (heuristic, engine), same as verdict — it is
//            not a property of the heuristic alone, so it renders inside that
//            engine's cell, never as a shared column. A missing/null value means
//            the backend hasn't scored it for that cell and renders as nothing,
//            never as "no impact" (that would be Rule 1's mistake again)
//   Rule 7 — an engine carrying `projection` describes a process state that does not
//            exist yet (its sub-processes redesigned too). The other engines describe
//            the process as it stands, so that column must be badged — an unlabelled
//            projection sitting next to two measurements is a misleading comparison

const ENGINE_LABEL = {
  greedy: "Process Optimization",
  rl: "RL Agent",
  bpr: "BPR Model",
};

const QUALITY_CONFIDENCE_STYLE = {
  analyst_confirmed: { bg: "#dcfce7", color: "#166534", border: "#86efac", label: "Analyst-confirmed" },
  mixed: { bg: "#fffbeb", color: "#b45309", border: "#fde68a", label: "Mixed confidence" },
  ai_suggested: { bg: "#fef3c7", color: "#92400e", border: "#fcd34d", label: "AI-suggested (unreviewed)" },
  unclassified: { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0", label: "Unclassified" },
};

const PROVENANCE_STYLE = {
  what_if: { bg: "#e0f2fe", color: "#075985", border: "#7dd3fc", label: "Measured" },
  native: { bg: "#fef9c3", color: "#854d0e", border: "#fde68a", label: "Engine-reported" },
  unscoreable: { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0", label: "Not comparable" },
};

const VERDICT_STYLE = {
  applied: { bg: "#dcfce7", color: "#166534", border: "#86efac", label: "✓ Applied" },
  recommended: { bg: "#fffbeb", color: "#b45309", border: "#fde68a", label: "Recommended" },
  rule_not_met: { bg: "#f8fafc", color: "#475569", border: "#cbd5e1", borderStyle: "dashed", label: "Rule not met" },
  unknown: { bg: "#f8fafc", color: "#94a3b8", border: "#e2e8f0", borderStyle: "dotted", label: "Unknown" },
  not_supported: { bg: "transparent", color: "#cbd5e1", border: "#e5e7eb", borderStyle: "dashed", label: "—" },
};

function Badge({ style, title, children }) {
  const s = style || VERDICT_STYLE.unknown;
  return (
    <span
      title={title}
      style={{
        display: "inline-block", fontSize: 10.5, fontWeight: 700, borderRadius: 999,
        padding: "2px 9px", whiteSpace: "nowrap", background: s.bg, color: s.color,
        border: `1px solid ${s.border}`, borderStyle: s.borderStyle || "solid",
      }}
    >
      {children}
    </span>
  );
}

// Rule 1 — a null to_be renders as an em-dash, never as 0 or blank. The reason
// (usually a baseline-drift %, e.g. this engine's own AS-IS is roll-up across
// sub-processes while the shared baseline is this process alone) is surfaced
// inline as a short caption, not just on hover — a hover-only tooltip proved too
// easy to miss and reads as "no effect" instead of "can't be honestly placed here".
function NotComparable({ reconciliation }) {
  const drift = reconciliation?.drift_pct;
  const reason = reconciliation?.reason;
  return (
    <span title={reason || "Could not be honestly placed on this axis"} style={{ cursor: reason ? "help" : "default" }}>
      <span style={{ color: "#9ca3af", fontWeight: 700 }}>— Not comparable</span>
      {drift != null && (
        <div style={{ fontSize: 10, color: "#d97706", fontWeight: 600, marginTop: 1 }}>
          {drift.toFixed(0)}% baseline drift
        </div>
      )}
    </span>
  );
}

// Generic improvement/regression badge for a devils_quadrangle metric object
// ({ delta, percent_change, improved }). Uses the backend's own `improved`
// flag rather than inferring direction, since which sign counts as "better"
// differs per axis (lower-is-better vs higher-is-better).
function DeltaBadge({ metric }) {
  if (!metric || metric.to_be == null) return null;
  const { delta, percent_change, improved } = metric;
  const color = improved === true ? "#059669" : improved === false ? "#dc2626" : "#6b7280";
  const glyph = improved === true ? "▼" : improved === false ? "▲" : "–";
  if (percent_change != null) {
    return <span style={{ fontWeight: 800, color }}>{glyph} {Math.abs(percent_change).toFixed(1)}%</span>;
  }
  // percent_change is always null when as_is is 0 by definition (e.g. flexibility) —
  // fall back to the raw delta rather than printing "null%".
  if (delta != null) {
    return <span style={{ fontWeight: 800, color }}>{glyph} {Math.abs(delta).toFixed(1)} pts</span>;
  }
  return <span style={{ color: "#9ca3af" }}>–</span>;
}

function QualityConfidenceBadge({ qc }) {
  if (!qc) return null;
  const style = QUALITY_CONFIDENCE_STYLE[qc.level] || QUALITY_CONFIDENCE_STYLE.unclassified;
  return (
    <Badge
      style={style}
      title={`${qc.by_analyst || 0} of ${qc.classified_task_count || 0} task(s) analyst-confirmed, ${qc.by_ai || 0} AI-suggested. The quality axis is a proxy over these task labels.`}
    >
      Quality basis: {style.label}
    </Badge>
  );
}

function ReportHeader({ result }) {
  const b = result.baseline || {};
  // The shared-baseline promise below holds for engines describing the process as it
  // stands. A projected column is measured against a different process, so the claim
  // has to be qualified rather than left standing for all three — but only where the
  // projection actually reached that column's figures.
  const withProjection = (result.engines || []).filter((e) => e && e.projection);
  const projected = withProjection
    .filter((e) => projectionPricedIntoKpis(e.projection, e.devils_quadrangle))
    .map((e) => ENGINE_LABEL[e.engine] || e.engine);
  const ranButUnpriced = withProjection
    .filter((e) => !projectionPricedIntoKpis(e.projection, e.devils_quadrangle))
    .map((e) => ENGINE_LABEL[e.engine] || e.engine);
  return (
    <div className="role-card" style={{ marginBottom: 16 }}>
      <div className="section-title">{result.process_name || "Process"} — shared baseline</div>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "baseline", marginBottom: 10 }}>
        <Stat label="Cycle time (AS-IS)" value={b.cycle_time_minutes != null ? `${b.cycle_time_minutes.toLocaleString()} min` : "—"} />
        <Stat label="Labor cost / case" value={b.labor_cost_per_case != null ? `${b.labor_cost_per_case.toLocaleString()} ${result.currency || "USD"}` : "—"} />
        <Stat label="Cycle-time efficiency" value={b.cycle_time_efficiency_percent != null ? `${b.cycle_time_efficiency_percent.toFixed(1)}%` : "—"} />
        <Stat label="Tasks" value={b.task_count ?? "—"} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <QualityConfidenceBadge qc={result.quality_confidence} />
        <span style={{ fontSize: 11, color: "#9ca3af" }}>
          Every engine&apos;s TO-BE below is measured against this one AS-IS — not its own.
        </span>
        {projected.length > 0 && (
          <span style={{ fontSize: 11, color: "#7c3aed", fontWeight: 600 }}>
            {projected.join(" and ")} additionally assumes this process&apos;s sub-processes are
            redesigned, so that column is a projection — see the badge on it.
          </span>
        )}
        {/* The shared-baseline promise above is still true of every row scored on it.
            The sub-process rows are not, so they are named here once rather than
            qualifying each column. */}
        {ranButUnpriced.length > 0 && (
          <span style={{ fontSize: 11, color: "#7c3aed", fontWeight: 600 }}>
            {ranButUnpriced.join(" and ")} also redesigned this process&apos;s sub-processes; those
            figures are listed separately under &ldquo;With sub-processes redesigned&rdquo; and sit on
            the engine&apos;s own scale.
          </span>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>{value}</div>
    </div>
  );
}

// KPI strip for one engine: time & cost only (quality/flexibility are estimated
// proxies, shown on the radar instead — see Rule 3).
//
// When the engine ran the sub-process redesigns but the backend scored its KPIs
// without them, the sub-process-inclusive figures are appended here as their own
// group rather than described in prose beside the table. They are what the analyst
// turned the flag on to see, so they belong in the numbers, and the Source column
// already exists to carry the fact that they sit on the engine's own scale.
function KpiStrip({ dq, scoring, money, fmtMin, projection, native }) {
  const rows = [
    { key: "time", label: "Cycle time", metric: dq?.time, fmt: (v) => fmtMin(v), method: scoring?.time, reconciliation: scoring?.time_reconciliation },
    { key: "cost", label: "Labor cost", metric: dq?.cost, fmt: (v) => money(v), method: scoring?.cost, reconciliation: scoring?.cost_reconciliation },
  ];

  // The same engine reports two different cost figures on two tabs — here it is every
  // RACI role (matching the CMS baseline every column is scored on), while its own
  // Redesign tab counts the Responsible role only. Both are right; side by side and
  // unlabelled they read as a bug. `labor_cost_scope` is the backend naming which
  // scale this column is on, so the note only appears when it actually says so.
  const costScope = native?.labor_cost_scope || null;
  const nativeResourceCost = native?.engine_headline_resource_cost;
  const withheld = rows.filter((r) => r.metric && r.metric.to_be == null && r.reconciliation?.reason);

  // Three conditions, all required, and the third is the one that bites:
  //   - a projection exists at all;
  //   - its child effect is NOT already inside the shared KPIs above (else this
  //     block would restate them);
  //   - the engine can actually produce a combined parent figure. Greedy cannot and
  //     says so via parent_rollup.available === false — and because its children
  //     carry no parent_impact, deriving one anyway yields a silent, confident
  //     "0.0%" row. That is worse than showing nothing, so it is gated out here and
  //     SubprocessChildResults renders the per-child measurements instead.
  const rollupBlocked = parentRollupUnavailableReason(projection);
  const projected = projection && !projectionPricedIntoKpis(projection, dq) && !rollupBlocked
    ? projectedParentKpis(projection)
    : null;
  // The cost field's actual scope depends on which engine sent it — RL's own
  // `resource_cost` counts the Responsible role only (a narrower figure than the
  // "Labor cost" row above, not a projected version of it), while BPR's
  // `labor_cost_per_case` is the same whole-process figure that row uses. Printing
  // both under one label would put RL's narrower number on the wrong shelf, so the
  // label — and a caveat where the number is narrower — follow `cost_scope`.
  const costIsWholeProcess = projected?.cost_scope === "labor_cost_per_case";
  const projectedRows = projected
    ? [
        { key: "p-time", label: "Cycle time", metric: projected.cycle_time, fmt: (v) => fmtMin(v) },
        {
          key: "p-cost",
          label: costIsWholeProcess ? "Labor cost" : "Resource cost",
          note: costIsWholeProcess ? null : "Responsible role only — narrower than the Labor cost row above, not a projected version of it.",
          metric: projected.resource_cost,
          fmt: (v) => money(v),
        },
      ].filter((r) => r.metric)
    : [];

  return (
    <>
      <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12, marginBottom: withheld.length ? 6 : 12 }}>
        <thead>
          <tr>
            {["", "AS-IS", "TO-BE", "Δ", "Source"].map((h) => (
              <th key={h || "label"} style={{ padding: "4px 8px", fontSize: 10, color: "#6b7280", textAlign: h ? "right" : "left", textTransform: "uppercase", borderBottom: "1.5px solid #e5e7eb" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const m = r.metric;
            const provStyle = PROVENANCE_STYLE[r.method] || PROVENANCE_STYLE.unscoreable;
            return (
              <tr key={r.key}>
                <td style={{ padding: "5px 8px", fontWeight: 600 }}>
                  {r.label}
                  {r.key === "cost" && costScope && (
                    <span
                      title={`This column counts ${costScope}, so it sits on the same scale as the shared baseline and the other engines.${
                        nativeResourceCost != null
                          ? ` This engine's own Redesign tab counts the Responsible role only and reports ${money(nativeResourceCost)} — a narrower measure of the same redesign, not a different result.`
                          : ""
                      }`}
                      style={{ marginLeft: 4, color: "#9ca3af", fontWeight: 700, cursor: "help" }}
                    >
                      ⓘ
                    </span>
                  )}
                </td>
                <td style={{ padding: "5px 8px", textAlign: "right" }}>{m?.as_is != null ? r.fmt(m.as_is) : "—"}</td>
                <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700 }}>
                  {m && m.to_be != null ? r.fmt(m.to_be) : <NotComparable reconciliation={r.reconciliation} />}
                </td>
                <td style={{ padding: "5px 8px", textAlign: "right" }}>
                  {m && m.to_be != null ? <DeltaBadge metric={m} /> : "—"}
                </td>
                <td style={{ padding: "5px 8px", textAlign: "right" }}>
                  <Badge style={provStyle} title={r.reconciliation?.reason}>{provStyle.label}</Badge>
                </td>
              </tr>
            );
          })}

          {projectedRows.length > 0 && (
            <tr>
              <td colSpan={5} style={{ padding: "7px 8px 3px", fontSize: 10, fontWeight: 800, color: "#5b21b6", textTransform: "uppercase", letterSpacing: "0.04em", borderTop: "1px solid #ede9fe" }}>
                With sub-processes redesigned
                <span style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: "#9ca3af" }}>
                  {" "}· {projected.redesigned_child_count} sub-process
                  {projected.redesigned_child_count === 1 ? "" : "es"}
                </span>
                {/* Rule 2 — this AS-IS is the engine's own measurement (its `native`
                    baseline), not the shared row above. Was hover-only on the
                    "Engine-reported" badge in the last column; sitting directly under
                    a row with the same AS-IS/TO-BE headers, it needs to be seen, not
                    found — see the RL vs BPR discrepancy this was written to explain. */}
                <div style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: "#9ca3af", marginTop: 2 }}>
                  AS-IS below is this engine&apos;s own measurement of the stored process, not the
                  shared baseline above — the two are computed differently and can legitimately differ.
                </div>
              </td>
            </tr>
          )}

          {projectedRows.map((r) => (
            <tr key={r.key} style={{ background: "#faf8ff" }}>
              <td style={{ padding: "5px 8px", fontWeight: 600 }}>
                {r.label}
                {r.note && (
                  <span title={r.note} style={{ marginLeft: 4, color: "#9ca3af", fontWeight: 700, cursor: "help" }}> ⓘ</span>
                )}
              </td>
              <td style={{ padding: "5px 8px", textAlign: "right" }}>{r.fmt(r.metric.as_is)}</td>
              <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700 }}>{r.fmt(r.metric.to_be)}</td>
              <td style={{ padding: "5px 8px", textAlign: "right" }}>
                {r.metric.reduction_pct != null ? (
                  <span style={{ fontWeight: 800, color: r.metric.saved > 0 ? "#059669" : "#6b7280" }}>
                    {r.metric.saved > 0 ? "▼" : "–"} {Math.abs(r.metric.reduction_pct).toFixed(1)}%
                  </span>
                ) : "—"}
              </td>
              <td style={{ padding: "5px 8px", textAlign: "right" }}>
                {/* Rule 2 — the engine's own scale. Kept out of the shared chart and
                    marked here, so these rows are never read as a cross-engine claim. */}
                <Badge
                  style={PROVENANCE_STYLE.native}
                  title="This engine's own measurement of the parent with every sub-process redesign adopted. Not on the shared baseline, so it is not comparable with the other engines."
                >
                  Engine-reported
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {/* Visible, not hover-only. The identical lesson is recorded on NotComparable
          above: a tooltip here proved too easy to miss, and the cost figure differing
          from the engine's own tab is exactly the kind of thing a reader files as a
          bug before they think to hover. Shown only when the engine actually reports
          a different own-scale figure — otherwise there is nothing to reconcile. */}
      {costScope && nativeResourceCost != null && dq?.cost?.to_be != null &&
        Math.abs(nativeResourceCost - dq.cost.to_be) > 0.005 && (
        <div style={{ marginBottom: 12, fontSize: 11, color: "#475569" }}>
          Cost here counts {costScope}. This engine&apos;s Redesign tab counts the Responsible role only
          and shows {money(nativeResourceCost)} — a narrower measure of the same redesign.
        </div>
      )}
      {withheld.length > 0 && (
        <div style={{ marginBottom: 12, padding: "6px 10px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, fontSize: 11, color: "#92400e" }}>
          {withheld.map((r) => (
            <div key={r.key}>
              <strong>{r.label} withheld:</strong> {r.reconciliation.reason}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function DqCoverageNote({ coverage }) {
  if (!coverage || coverage.complete !== false) return null;
  const items = coverage.not_represented || [];
  const represented = coverage.represented || [];
  // Rule 4, sharpened — when most of what the engine applied cannot be priced, the
  // column understates it badly (BPR on 1979: 1 of 5 priced, a 19.9% cost claim
  // reduced to 2.2% here). A collapsed <details> is the wrong weight for that, so the
  // headline of the warning is always visible and only the per-heuristic reasons fold.
  const severe = items.length > represented.length;
  return (
    <div style={{ marginBottom: 10, fontSize: 11.5 }}>
      <div style={{ padding: "7px 10px", background: severe ? "#fffbeb" : "transparent", border: severe ? "1px solid #fde68a" : "none", borderRadius: 8, color: "#92400e", fontWeight: 700 }}>
        ⚠ Partial quadrangle — only {represented.length} of {represented.length + items.length} applied
        heuristic{represented.length + items.length === 1 ? "" : "s"} could be priced into the figures above.
        {severe && (
          <div style={{ fontWeight: 400, marginTop: 3 }}>
            This column understates what this engine proposes. Compare it on the heuristics matrix
            below, not on these numbers alone.
          </div>
        )}
      </div>
      <details style={{ marginTop: 4 }}>
        <summary style={{ cursor: "pointer", color: "#b45309", fontWeight: 700 }}>
          What could not be priced
        </summary>
        <div style={{ marginTop: 6, padding: "8px 10px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, color: "#92400e" }}>
          <ul style={{ margin: 0, padding: "0 0 0 16px" }}>
            {items.map((it, i) => (
              <li key={i} style={{ marginBottom: 4 }}><strong>{it.code}:</strong> {it.reason}</li>
            ))}
          </ul>
        </div>
      </details>
    </div>
  );
}

function NativeSection({ native, currency, fmtMin, money }) {
  if (!native) return null;
  return (
    <details style={{ marginTop: 10, fontSize: 11.5 }}>
      <summary style={{ cursor: "pointer", color: "#6b7280", fontWeight: 700 }}>
        Engine self-report (audit only — own scale, own baseline)
      </summary>
      <div style={{ marginTop: 6, padding: "8px 10px", background: "#fafafa", border: "1px solid #eef2f7", borderRadius: 8, color: "#475569" }}>
        <div>AS-IS (engine's own): {native.as_is_cycle_time_minutes != null ? fmtMin(native.as_is_cycle_time_minutes) : "—"} · {native.as_is_labor_cost_per_case != null ? money(native.as_is_labor_cost_per_case) : "—"}</div>
        <div>TO-BE (engine's own): {native.cycle_time_minutes != null ? fmtMin(native.cycle_time_minutes) : "—"} · {native.labor_cost_per_case != null ? money(native.labor_cost_per_case) : "—"}</div>
        <div style={{ marginTop: 4, fontSize: 10.5, color: "#9ca3af" }}>
          Not comparable across engines — never plotted next to the shared chart above.
        </div>
      </div>
    </details>
  );
}

function EngineDetailFooter({ detail }) {
  if (!detail) return null;
  return (
    <div style={{ marginTop: 10, fontSize: 10.5, color: "#9ca3af" }}>
      {detail.algorithm}
      {detail.episodes != null ? ` · ${detail.episodes} episodes` : ""}
      {/* The scenario the engine's TO-BE was priced from. When every engine lands on
          the same one (commonly "Eliminate NVA steps"), this is the only field that
          explains why three columns show identical figures. */}
      {detail.active_scenario ? ` · scored from “${detail.active_scenario}”` : ""}
      {detail.deterministic === false && (
        <span style={{ color: "#b45309", fontWeight: 700 }}> · may vary between runs</span>
      )}
    </div>
  );
}

// Rule 8 — three columns of identical numbers read as a bug. They are not: every
// engine's scored TO-BE can resolve to the same structural change (typically the
// shared CMS non-value-adding elimination, which no engine discovered on its own).
// Saying so is the difference between a converged result and a broken page.
function ConvergenceNote({ engines }) {
  const ok = (engines || []).filter((e) => e && e.status !== "failed" && e.kpis);
  if (ok.length < 2) return null;

  const key = (e) => `${e.kpis.cycle_time_minutes}|${e.kpis.labor_cost_per_case}`;
  const first = key(ok[0]);
  if (!ok.every((e) => key(e) === first)) return null;

  const scenarios = [...new Set(ok.map((e) => e.engine_detail?.active_scenario).filter(Boolean))];

  return (
    <div style={{ marginBottom: 14, padding: "8px 12px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, fontSize: 12.5, color: "#075985" }}>
      <strong>All {ok.length} engines converged on the same scored result.</strong> The figures
      below are identical because each engine&apos;s TO-BE prices out to the same structural
      change{scenarios.length === 1 ? ` (“${scenarios[0]}”)` : ""} — not because the comparison
      failed to run. Where that change is a removal of steps CMS already classified
      non-value-adding, all three consumed the same classification rather than finding it
      independently.
    </div>
  );
}

function EngineColumn({ engineKey, engine, money, fmtMin }) {
  const label = ENGINE_LABEL[engineKey] || engineKey;

  if (engine.status === "failed") {
    // `error` is a plain string for most failures but, like the other pipelines'
    // detail objects, can arrive as { error, message, issues } for a validation
    // failure — never render it raw, React can't render an object as a child.
    const errObj = engine.error && typeof engine.error === "object" ? engine.error : null;
    const errorText = errObj
      ? errObj.message || errObj.error || "This engine did not return a result."
      : engine.error || "This engine did not return a result.";
    const issues = Array.isArray(errObj?.issues) ? errObj.issues : [];
    return (
      <div style={{ flex: "1 1 320px", minWidth: "min(300px, 100%)", border: "1px solid #fca5a5", borderRadius: 12, padding: "12px 14px", background: "#fef2f2" }}>
        <div style={{ fontWeight: 800, fontSize: 13.5, color: "#374151", marginBottom: 6 }}>{label}</div>
        <div style={{ color: "#991b1b", fontSize: 12.5 }}>
          <strong>Failed:</strong> {errorText}
        </div>
        {issues.length > 0 && (
          <ul style={{ margin: "6px 0 0 18px", padding: 0, fontSize: 11.5, color: "#991b1b" }}>
            {issues.map((it, i) => (
              <li key={i}>{typeof it === "string" ? it : it?.message || JSON.stringify(it)}</li>
            ))}
          </ul>
        )}
        {engineKey === "bpr" && (
          <div style={{ marginTop: 6, fontSize: 11, color: "#9ca3af" }}>
            BPR calls a third-party host — this is routine, not exceptional.
          </div>
        )}
      </div>
    );
  }

  const dq = engine.devils_quadrangle;
  const axes = axesFromDevilsQuadrangle(dq);

  // Rule 7, corrected — the presence of `projection` is NOT the signal. All three
  // engines now ship the block whenever the flag is on, and they disagree about what
  // it means: the backend's own docs say to read `kpis_are_projected` rather than
  // infer from the block being there. projectionPricedIntoKpis() reads that field.
  const projection = engine.projection || null;
  const projectionPriced = projectionPricedIntoKpis(projection, dq);
  // Greedy: sub-process work was done and measured, but this column can show no
  // aggregate of it at all. Without the per-child cards below, that column would
  // look as though the flag had done nothing.
  const rollupBlocked = parentRollupUnavailableReason(projection);

  return (
    <div style={{ flex: "1 1 380px", minWidth: "min(340px, 100%)", border: `1px solid ${projection ? (projectionPriced ? "#ddd6fe" : "#fde68a") : "#eef2f7"}`, borderRadius: 12, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 800, fontSize: 13.5, color: "#374151" }}>{label}</div>
        {/* Only the priced case gets a column-level badge. When the projection is not
            priced in, the shared rows below are ordinary measurements and badging the
            whole column would mislabel them; the sub-process figures carry their own
            labelled group inside the strip instead. */}
        {projection && projectionPriced && <ProjectedColumnBadge />}
        {dq && dq.counts && (dq.counts.parallel_task_count > 0 || dq.counts.substitutable_task_count > 0) && (
          <span style={{ fontSize: 10.5, color: "#9ca3af" }}>
            {dq.counts.parallel_task_count} parallel · {dq.counts.substitutable_task_count} substitutable
          </span>
        )}
      </div>

      {/* The standard banner opens with "Projected figures", which is true only when
          the projection reached the numbers. When it did not, the KPI strip's own
          "With sub-processes redesigned" group below already says so. */}
      {projectionPriced && <ProjectionBanner projection={projection} compact />}

      <KpiStrip dq={dq} scoring={engine.scoring} money={money} fmtMin={fmtMin} projection={projection} native={engine.native} />

      {rollupBlocked && (
        <SubprocessChildResults
          projection={projection}
          fmtMin={fmtMin}
          money={money}
          title="SUB-PROCESSES — measured individually"
        />
      )}

      {axes && (
        <>
          <DevilsQuadrangleChart axes={axes} />
          <div style={{ marginTop: 4, fontSize: 10.5, color: "#9ca3af" }}>
            Time &amp; Cost are backend-priced (measured). Quality &amp; Flexibility are deterministic
            estimates over rework / non-value-adding share — treat them as a proxy, not a priced figure.
          </div>
        </>
      )}

      <DqCoverageNote coverage={engine.dq_coverage} />
      <NativeSection native={engine.native} money={money} fmtMin={fmtMin} />
      <EngineDetailFooter detail={engine.engine_detail} />
    </div>
  );
}

function VerdictChip({ cell }) {
  if (!cell) return <Badge style={VERDICT_STYLE.not_supported}>{VERDICT_STYLE.not_supported.label}</Badge>;
  const style = VERDICT_STYLE[cell.verdict] || VERDICT_STYLE.unknown;
  const taskNote = (cell.task_ids || []).length > 0 ? ` · ${cell.task_ids.length}` : "";
  return <Badge style={style}>{style.label}{taskNote}</Badge>;
}

// A small dot, not a second badge — see Rule 6. Was a full pill plus its own
// always-visible explanation paragraph, which meant every cell carried three
// stacked blocks of text of near-equal visual weight (verdict, this, the reason
// below) and none of them read as more important than the others. The explanation
// is still there, just on hover: it is boilerplate that repeats near-verbatim across
// a column ("the task node is removed... re-analysed..."), so scanning down a column
// only needs the dot's color, not the sentence, every single row.
function ImpactDot({ cell }) {
  if (!cell || cell.impact_computable == null) return null;
  const on = cell.impact_computable;
  return (
    <span
      title={cell.impact_note ? `${on ? "Measurable now" : "No immediate change"} — ${cell.impact_note}` : (on ? "Measurable now" : "No immediate change")}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700,
        color: on ? "#15803d" : "#94a3b8", cursor: "help", whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: on ? "#22c55e" : "#cbd5e1", flexShrink: 0 }} />
      {on ? "measurable" : "no change"}
    </span>
  );
}

// One matrix cell: a badge row that stays on one visual line (verdict, whether it's
// measurable, which sub-process it came from) and, only when there is one, the
// measured before/after sentence below it — set off with a left rule so it reads as
// supporting detail rather than a fourth peer element. This is the one piece of text
// per cell that must stay always-visible, not a tooltip: it is where the NVA
// provenance and the actual numbers live (see the comment this replaces).
// `childNames` is the full list this heuristic traces back to — never a headcount
// standing in for names the map already knows. One chip per name, so "applied inside
// 3 sub-processes" reads as which 3, not a number the reader has to go elsewhere for.
function MatrixCell({ cell, childNames }) {
  const reason = cell?.verdict === "applied" ? cell.reason : null;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
        <VerdictChip cell={cell} />
        <ImpactDot cell={cell} />
      </div>
      {childNames && childNames.length > 0 && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
          {childNames.map((name) => <ChildChip key={name} name={name} />)}
        </div>
      )}
      {reason && (
        <div style={{ marginTop: 5, paddingLeft: 8, borderLeft: "2px solid #e5e7eb", fontSize: 11, lineHeight: 1.45, color: "#6b7280", maxWidth: 260 }}>
          {reason}
        </div>
      )}
    </div>
  );
}

function HeuristicMatrix({ matrix, engineKeys, engines }) {
  // Which (engine, heuristic) cells are sub-process results rather than something
  // the engine did to this process. Without this the "engines agree on X" banner can
  // credit an engine with applying X here when it applied it inside a child.
  //
  // Two sources, tried in order: `subprocessBadgesByHeuristic` reads per-row
  // source="subprocess" data (RL sends this); `subprocessBadgesByCodeFromProjection`
  // falls back to `projection.children[].applied_heuristics` for an engine whose
  // comparison response never breaks heuristics out by row at all (BPR today).
  const badgesByEngine = React.useMemo(() => {
    const out = {};
    for (const engine of engines || []) {
      const byName = subprocessBadgesByHeuristic(engine);
      if (byName.size > 0) { out[engine.engine] = { map: byName, kind: "name" }; continue; }
      const byCode = subprocessBadgesByCodeFromProjection(engine);
      if (byCode.size > 0) out[engine.engine] = { map: byCode, kind: "code" };
    }
    return out;
  }, [engines]);

  const childBadgeFor = (engineKey, row) => {
    const entry = badgesByEngine[engineKey];
    if (!entry) return undefined;
    return entry.kind === "name"
      ? lookupSubprocessBadge(entry.map, row.name)
      : lookupSubprocessBadgeByCode(entry.map, row.code);
  };

  if (!Array.isArray(matrix) || matrix.length === 0) {
    return <div className="no-results">No heuristics reported.</div>;
  }

  // The backend's `agreement` counts any "applied" cell, including one an engine
  // reached only inside a sub-process. Two engines applying a heuristic to two
  // different processes is not agreement about this one, so the banner counts only
  // cells that acted on this process.
  const parentAgreement = (row) =>
    Object.entries(row.engines || []).filter(
      ([k, cell]) => cell?.verdict === "applied" && !childBadgeFor(k, row),
    ).length;

  const agreedRows = matrix.filter((r) => parentAgreement(r) >= 2);
  // Removing steps CMS already flagged is the one thing every engine does from the
  // same input, so it can reach agreement 3 without any engine having discovered it.
  const sharedInputRows = agreedRows.filter((r) => r.code === "activity-elimination");

  return (
    <div>
      {agreedRows.length > 0 && (
        <div style={{ marginBottom: 10, padding: "8px 12px", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 10, fontSize: 12.5, color: "#0e6b68" }}>
          <strong>Engines agree on:</strong> {agreedRows.map((r) => r.name).join(", ")} — each applied
          to this process by 2+ engines.
          {sharedInputRows.length > 0 && (
            <div style={{ marginTop: 4, fontSize: 11.5 }}>
              Note that where a heuristic removes steps CMS has already classified
              non-value-adding ({sharedInputRows.map((r) => r.name).join(", ")}), the engines are
              consuming the same classification — that is a shared input, not independent
              discovery, and is weaker evidence than the rest of this list.
            </div>
          )}
        </div>
      )}
      <div style={{ overflowX: "auto", border: "1px solid #eef2f7", borderRadius: 12 }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 700, fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ padding: "9px 12px", fontSize: 10.5, fontWeight: 700, color: "#6b7280", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.03em", borderBottom: "1.5px solid #e5e7eb", borderRight: "1px solid #eef2f7", position: "sticky", left: 0, background: "#f8fafc", zIndex: 1 }}>Heuristic</th>
              {engineKeys.map((k, i) => (
                <th key={k} style={{ padding: "9px 12px", fontSize: 10.5, fontWeight: 700, color: "#6b7280", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.03em", borderBottom: "1.5px solid #e5e7eb", borderRight: i === engineKeys.length - 1 ? "none" : "1px solid #eef2f7", background: "#f8fafc" }}>{ENGINE_LABEL[k] || k}</th>
              ))}
              <th style={{ padding: "9px 12px", fontSize: 10.5, fontWeight: 700, color: "#6b7280", textAlign: "center", textTransform: "uppercase", letterSpacing: "0.03em", borderBottom: "1.5px solid #e5e7eb", background: "#f8fafc", width: 92 }}>Agreement</th>
            </tr>
          </thead>
          <tbody>
            {matrix.map((row, idx) => {
              const onThisProcess = parentAgreement(row);
              const agreed = onThisProcess >= 2;
              // The gap between the backend's count and this one is the number of
              // engines that reached the heuristic only inside a sub-process.
              const inChildrenOnly = Math.max(0, (row.agreement || 0) - onThisProcess);
              const isLast = idx === matrix.length - 1;
              return (
                <tr key={row.code} style={{ verticalAlign: "top", background: agreed ? "#f0fdfa" : idx % 2 ? "#fafbfc" : "#fff" }}>
                  <td style={{ padding: "11px 12px", fontWeight: 700, borderRight: "1px solid #eef2f7", borderBottom: isLast ? "none" : "1px solid #f1f5f9", position: "sticky", left: 0, background: "inherit", ...(agreed ? { boxShadow: "inset 3px 0 0 #0e6b68" } : {}) }}>
                    {row.name}
                    <div style={{ fontSize: 9.5, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.02em", marginTop: 1 }}>{String(row.category || "").replace(/_/g, " ")}</div>
                  </td>
                  {engineKeys.map((k, i) => (
                    <td key={k} style={{ padding: "11px 12px", borderRight: i === engineKeys.length - 1 ? "none" : "1px solid #f1f5f9", borderBottom: isLast ? "none" : "1px solid #f1f5f9" }}>
                      <MatrixCell cell={row.engines?.[k]} childNames={childBadgeFor(k, row)} />
                    </td>
                  ))}
                  <td style={{ padding: "11px 12px", textAlign: "center", borderBottom: isLast ? "none" : "1px solid #f1f5f9" }}>
                    {agreed ? (
                      <Badge style={{ bg: "#0e6b68", color: "#fff", border: "#0e6b68" }}>{onThisProcess}</Badge>
                    ) : (
                      <span style={{ color: "#9ca3af", fontWeight: 600 }}>{onThisProcess}</span>
                    )}
                    {inChildrenOnly > 0 && (
                      <div
                        title="Applied by this many further engines, but only inside a sub-process — not to this process."
                        style={{ fontSize: 9.5, color: "#7c3aed", fontWeight: 700, marginTop: 4, cursor: "help", lineHeight: 1.3 }}
                      >
                        +{inChildrenOnly} sub-process
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ComparisonReportPanel({ processId, money, fmtMin, result, running, runError, run }) {
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [seed, setSeed] = React.useState("");
  // On by default (see DEFAULT_INCLUDE_SUBPROCESS). It now applies to all three
  // columns, not just RL — each reports what it did inside every sub-process under
  // engines[].projection.
  const [includeSubprocesses, setIncludeSubprocesses] = React.useState(DEFAULT_INCLUDE_SUBPROCESS);

  if (!processId) return null;

  const doRun = () => {
    const opts = {};
    if (seed !== "" && Number.isFinite(Number(seed))) opts.seed = Number(seed);
    if (includeSubprocesses) opts.includeSubprocessRedesigns = true;
    run(processId, opts);
  };

  const engineKeys = Array.isArray(result?.engines) ? result.engines.map((e) => e.engine) : [];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <button type="button" className="primary-btn sm" disabled={running} onClick={doRun}>
          {running ? "Comparing engines…" : result ? "Re-run comparison" : "Compare"}
        </button>
        <button type="button" className="secondary-btn sm" onClick={() => setShowAdvanced((v) => !v)}>
          {showAdvanced ? "Hide options" : "Options"}
        </button>
        <span style={{ fontSize: 11, color: "#9ca3af" }}>runs greedy, RL and BPR concurrently — typically 15–40s</span>
      </div>

      <label
        style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12, fontSize: 12, color: "#374151", cursor: "pointer" }}
        title="Every column redesigns each sub-process and reports what it applied inside it. The shared KPIs still describe this process with its sub-processes as stored — see each column's sub-process section."
      >
        <input
          type="checkbox"
          checked={includeSubprocesses}
          disabled={running}
          onChange={(e) => setIncludeSubprocesses(e.target.checked)}
        />
        {/* Was "the RL column only" — the flag now applies to all three engines. */}
        <span>Redesign sub-processes too (all columns)</span>
        <span style={{ fontSize: 11, color: "#9ca3af" }}>
          adds a run per sub-process on every engine
        </span>
      </label>

      {showAdvanced && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, fontSize: 12 }}>
          <label style={{ color: "#6b7280" }}>
            RL seed (optional — pins the RL column so it's reproducible; without it RL is stochastic)
          </label>
          <input
            type="number"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            placeholder="none"
            style={{ width: 100, fontSize: 12, padding: "3px 8px", border: "1px solid #d1d5db", borderRadius: 8 }}
          />
        </div>
      )}

      {running && (
        <div className="no-results">
          {includeSubprocesses
            ? "Running greedy, RL and BPR against one shared baseline, plus a run per sub-process on each — this can take a couple of minutes…"
            : "Running greedy, RL and BPR against one shared baseline — this can take up to 40s…"}
        </div>
      )}

      {runError && (
        <div style={{ padding: "12px 16px", marginBottom: 14, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 10, color: "#991b1b", fontSize: 13 }}>
          <strong>Comparison report failed:</strong> {runError}
        </div>
      )}

      {!result && !running && !runError && (
        <div className="no-results">Run the comparison to see greedy, RL and BPR side by side against one shared baseline.</div>
      )}

      {result && (
        <div>
          <ReportHeader result={result} />

          <ConvergenceNote engines={result.engines} />

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 20 }}>
            {(result.engines || []).map((engine) => (
              <EngineColumn key={engine.engine} engineKey={engine.engine} engine={engine} money={money} fmtMin={fmtMin} />
            ))}
          </div>

          <div className="section-title">Heuristics matrix</div>
          <HeuristicMatrix matrix={result.heuristic_matrix} engineKeys={engineKeys} engines={result.engines} />
        </div>
      )}
    </div>
  );
}
